import moment from "moment";
import { METODI_PAGAMENTO, nomeMetodoPagamento } from "./metodoPagamento";

export default {
  props: {
    contabile: Object,
  },
  data() {
    return {
      dialog: false,
      loading: false,
      loadingSalva: false,
      loadingElimina: null, // id dell'allocazione in eliminazione, o null
      comeDebito: [],
      comeCredito: [],
      importoBase: null,
      residuo: null,
      creditiDisponibili: [],
      fonte: "pagamento", // "pagamento" | "credito"
      metodoPagamento: METODI_PAGAMENTO[0].value,
      idCreditoSelezionato: null,
      importoDaAllocare: null,
      // Data di saldo della copertura (round 65-septies): di default oggi
      // (il caso più comune — si registra una copertura appena avvenuta),
      // ma modificabile per registrare un incasso con data diversa da
      // oggi (es. inserito in ritardo). "YYYY-MM-DD" per il date-picker.
      dataSaldo: moment().format("YYYY-MM-DD"),
      menuDataSaldo: false,
      note: null,
      warningMsg: null,
      errorMsg: null,
      metodiPagamento: METODI_PAGAMENTO,
      // "Estingui credito residuo": per il caso in cui quella parte non
      // verrà mai erosa da un debito reale (es. una Fattura Anticipata di
      // cui il cliente salda solo l'IVA e la questione finisce lì).
      dialogEstingui: false,
      loadingEstingui: false,
      // Pagamento a rate di una Fattura Anticipata/Posticipata (caso raro
      // — pochi clienti): imponibile+IVA per intero, sempre incasso
      // fresco (mai erosione di un altro credito). Le rate registrate
      // sono già in comeDebito (get_allocazioni_contabile le restituisce
      // lì per qualunque contabile, fattura inclusa). Un'unica via per
      // crearne (anche una sola): un piano di rate pianificate (non
      // ancora pagate) spuntate poi man mano che il cliente paga davvero
      // — niente più scorciatoia per registrarne una già incassata al
      // volo (round 56-quater, semplificazione voluta dall'utente).
      importoTotalePagamento: null,
      incassatoPagamento: null,
      residuoPagamento: null,
      // scadenzaIniziale/intervalloGiorni (round 56-ter) sono opzionali:
      // se valorizzata la prima, ogni rata generata nasce con una
      // scadenza distanziata di quell'intervallo.
      numeroRatePianificate: 1,
      scadenzaIniziale: null, // "YYYY-MM-DD" o null
      intervalloGiorni: 30,
      menuScadenzaIniziale: false,
      loadingSuddividi: false,
      loadingSaldatoRata: null, // id della rata in aggiornamento, o null
      // Scadenza di una singola rata già registrata: quale menu di
      // modifica è aperto (id della rata, o null) — un solo v-menu alla
      // volta, tenuto in comune per tutte le righe della lista.
      menuScadenzaRataAperto: null,
      loadingScadenzaRata: null,
      // Data di incasso (ts_saldo) di una rata già saldata — stesso
      // pattern "un solo menu alla volta" della scadenza sopra (round
      // 65-undecies).
      menuTsSaldoRataAperto: null,
      loadingTsSaldoRata: null,
      // Conferma di eliminazione di una rata (round 65-undecies): prima
      // non c'era alcuna conferma, un click cancellava subito.
      rataDaEliminare: null,
      dialogConfermaEliminaRata: false,
      // Data di una copertura "normale" già registrata (non una rata) in
      // "Coperture registrate" — stesso pattern delle rate, round
      // 65-octovicies.
      menuTsCoperturaAperto: null,
      loadingTsCopertura: null,
      // Metodo di pagamento di una copertura "normale" già registrata —
      // round 65-tricies.
      loadingMetodoCopertura: null,
    };
  },
  computed: {
    isDebito() {
      return this.contabile.tipo === 1;
    },
    isCredito() {
      return this.contabile.tipo === 2 || this.contabile.tipo === 3;
    },
    // Solo una Fattura Anticipata/Posticipata può essere pagata a rate
    // (round 53-bis) — una nota di credito (tipo=2) è sempre saldata per
    // definizione, non ha senso rateizzarla.
    isFatturaARate() {
      return this.contabile.tipo === 3;
    },
    importoDebito() {
      return Math.abs(this.contabile.importo || 0);
    },
    clienteNome() {
      const cliente = this.$store.getters.get_cliente_by_iva(this.contabile.cliente);
      return cliente ? cliente.nome : this.contabile.cliente;
    },
    // `contabile.n_ordine` non è affidabile (mai popolato in creazione,
    // vedi create_contabile): il numero ordine vero si legge dalla stessa
    // cache già riempita in blocco dalla lista principale
    // (fetch_ordini_numeri, vedi ContabileOrdineRef) — nessuna richiesta
    // di rete aggiuntiva se il dialog si apre da lì, come sempre accade.
    numeroOrdine() {
      if (!this.contabile.id_ordine) return null;
      const cached = this.$store.getters.get_ordine_numero_by_id(this.contabile.id_ordine);
      return cached ? cached.numero : null;
    },
    totaleAllocatoDebito() {
      return this.comeDebito.reduce((tot, a) => tot + a.importo, 0);
    },
    // Arrotondato a 2 decimali (round 65-ter): una sottrazione diretta tra
    // float JS può lasciare residui di imprecisione (es. 29.999999999999996)
    // che, precompilati nel campo "Importo da allocare" sotto, comparivano
    // per intero invece di un pulito "29,99"/"30,00".
    residuoDebito() {
      return this.roundToTwo(
        Math.max(0, this.importoDebito - this.totaleAllocatoDebito)
      );
    },
    creditoSelezionato() {
      return this.creditiDisponibili.find((c) => c.id === this.idCreditoSelezionato);
    },
    // Una Fattura Anticipata/Posticipata (tipo=3) eroda sempre e solo
    // debiti verdi (regolarizza ritiri fuori tracciabilità DDT): compare
    // come fonte solo se questo debito è verde, indipendentemente dal suo
    // proprio verde (sempre blu/formale) — non la regola simmetrica valida
    // per un Pagamento Cliente (tipo=2), dove serve la stessa modalità.
    // Entrambe le fonti sono ora pagamenti freschi tracciati, componibili
    // tra loro: un debito alto può essere coperto in più pezzi da fonti
    // diverse (es. metà da una fattura, metà da un'altra o da un
    // Pagamento Cliente) — round 44.
    creditiDisponibiliCoerenti() {
      return this.creditiDisponibili.filter((c) =>
        c.tipo === 3 ? this.contabile.verde === true : !!c.verde === !!this.contabile.verde
      );
    },
    // Disponibile finché c'è ancora qualcosa da coprire (residuoDebito >
    // 0) e almeno un credito coerente da cui attingere — componibile con
    // altre coperture già registrate, anche da fonti diverse (un debito
    // alto può essere coperto in più pezzi, es. metà da una fattura,
    // metà da un'altra o da un Pagamento Cliente).
    erosioneDisponibile() {
      return (
        this.isDebito &&
        this.residuoDebito > 0 &&
        this.creditiDisponibiliCoerenti.length > 0
      );
    },
    fonteOptions() {
      const opzioni = [{ value: "pagamento", text: "Pagamento fresco" }];
      if (this.erosioneDisponibile) {
        opzioni.push({ value: "credito", text: "Credito residuo esistente" });
      }
      return opzioni;
    },
    // Per il campo "Prima scadenza" di "Suddividi in X rate" (round 56-ter).
    scadenzaInizialeFormatted() {
      return this.scadenzaIniziale
        ? moment(this.scadenzaIniziale).format("DD/MM/YYYY")
        : "";
    },
    // Per il campo "Data" di "Registra copertura" (round 65-septies).
    dataSaldoFormatted() {
      return this.dataSaldo ? moment(this.dataSaldo).format("DD/MM/YYYY") : "";
    },
  },
  watch: {
    fonte(valore) {
      if (valore === "pagamento") {
        this.importoDaAllocare = this.residuoDebito || null;
        // Un pagamento fresco è un incasso che avviene ora — la data
        // torna a oggi (round 65-nonies), coerente con quella impostata
        // all'apertura del dialog.
        this.dataSaldo = moment().format("YYYY-MM-DD");
      }
      // Per "credito" l'importo E la data di default vengono impostati dal
      // watcher su idCreditoSelezionato, una volta scelto il credito da
      // erodere.
    },
    idCreditoSelezionato(valore) {
      if (this.fonte !== "credito" || !valore) {
        return;
      }
      const credito = this.creditiDisponibili.find((c) => c.id === valore);
      if (!credito) {
        return;
      }
      // Pagamento fresco parziale per qualunque fonte (Pagamento Cliente
      // o Fattura Anticipata, round 44): precompilato al minimo tra
      // quanto manca sul debito e quanto resta sul credito, sempre
      // editabile.
      this.importoDaAllocare = Math.min(this.residuoDebito, credito.residuo);
      // La data di saldo rispecchia quella del credito erodibile (round
      // 65-nonies): erodere un Pagamento Cliente (sempre saldato) o una
      // Fattura di Copertura già saldata non è un nuovo incasso — è
      // denaro arrivato in passato, in quella data, solo riassegnato ora
      // a questo debito. Una Fattura di Copertura NON ancora saldata
      // invece non ha nessuna data reale da riflettere (nulla è stato
      // ancora incassato): il campo resta vuoto, editabile a mano se
      // serve.
      if (credito.tipo === 3 && !credito.saldato) {
        this.dataSaldo = null;
      } else {
        const riferimento = credito.ts_saldo || credito.ts;
        this.dataSaldo = riferimento
          ? moment.unix(riferimento).format("YYYY-MM-DD")
          : null;
      }
    },
  },
  methods: {
    roundToTwo(num) {
      return Math.round((num + Number.EPSILON) * 100) / 100;
    },
    nomeMetodo(value) {
      return nomeMetodoPagamento(value);
    },
    // item-text esplicito per il select "Credito da erodere": senza,
    // v-select usa internamente la proprietà "text" (che questi oggetti
    // non hanno) e in alcuni punti finisce per mostrare "[object Object]"
    // invece del contenuto degli slot item/selection.
    testoCredito(item) {
      const tipo = item.tipo === 3 ? "Fattura di copertura" : "Pagamento Cliente";
      const ddt = item.ddt ? " — DDT " + item.ddt : "";
      return `${tipo}${ddt} — residuo € ${item.residuo.toFixed(2)}`;
    },
    formatTs(ts) {
      return ts ? moment.unix(ts).format("DD/MM/YYYY") : "";
    },
    // Riferimento alla fonte di una copertura (comeDebito) quando è un
    // pagamento fresco tracciato che consuma un credito esistente — usato
    // per decidere se mostrare il badge saldato/non saldato quando la
    // fonte è una Fattura Anticipata.
    fonteAllocazione(a) {
      return a.pagamento_origine || null;
    },
    open() {
      this.dialog = true;
      this.warningMsg = null;
      this.errorMsg = null;
      this.fonte = "pagamento";
      this.idCreditoSelezionato = null;
      this.dataSaldo = moment().format("YYYY-MM-DD");
      this.caricaDati();
    },
    // `silenzioso` (round 65-duodecies): un refresh dopo un'azione che ha
    // già un proprio indicatore locale (checkbox/data-picker/pulsante con
    // :loading) non deve rimpiazzare tutto il corpo del dialog con lo
    // spinner grande — solo il primissimo caricamento all'apertura (dove
    // non c'è ancora nulla da mostrare) resta "rumoroso".
    caricaDati(silenzioso = false) {
      if (!silenzioso) {
        this.loading = true;
      }
      const richieste = [
        this.$store.dispatch("fetch_allocazioni_contabile", this.contabile.id),
      ];
      if (this.isDebito) {
        richieste.push(
          this.$store.dispatch(
            "fetch_crediti_disponibili_cliente",
            this.contabile.cliente
          )
        );
      }
      Promise.all(richieste)
        .then(([datiAllocazioni, crediti]) => {
          this.comeDebito = datiAllocazioni.come_debito || [];
          this.comeCredito = datiAllocazioni.come_credito || [];
          this.importoBase = datiAllocazioni.importo_base ?? null;
          this.residuo = datiAllocazioni.residuo ?? null;
          this.importoTotalePagamento = datiAllocazioni.importo_totale_pagamento ?? null;
          this.incassatoPagamento = datiAllocazioni.incassato_pagamento ?? null;
          this.residuoPagamento = datiAllocazioni.residuo_pagamento ?? null;
          if (this.isDebito) {
            this.creditiDisponibili = crediti || [];
            this.importoDaAllocare = this.residuoDebito || null;
          }
        })
        .catch(() => {
          this.errorMsg = "Errore nel caricamento dello storico allocazioni";
        })
        .finally(() => {
          if (!silenzioso) {
            this.loading = false;
          }
        });
    },
    registraAllocazione() {
      if (!this.importoDaAllocare || this.importoDaAllocare <= 0) {
        this.errorMsg = "Inserisci un importo maggiore di zero";
        return;
      }
      if (
        this.fonte === "credito" &&
        this.creditoSelezionato &&
        this.importoDaAllocare > this.creditoSelezionato.residuo
      ) {
        this.errorMsg = `Importo superiore al residuo disponibile su questo credito (€ ${this.creditoSelezionato.residuo.toFixed(2)})`;
        return;
      }
      this.loadingSalva = true;
      this.errorMsg = null;
      this.warningMsg = null;
      this.$store
        .dispatch("crea_allocazione", {
          id_debito: this.contabile.id,
          importo: this.importoDaAllocare,
          id_credito: this.fonte === "credito" ? this.idCreditoSelezionato : null,
          metodo_pagamento: this.fonte === "pagamento" ? this.metodoPagamento : null,
          note: this.note,
          ts: this.dataSaldo ? moment(this.dataSaldo).unix() : undefined,
        })
        .then((data) => {
          if (data.warning) {
            this.warningMsg = data.warning;
          }
          this.note = null;
          this.idCreditoSelezionato = null;
          this.caricaDati(true);
          this.$emit("cambiato");
        })
        .catch((err) => {
          this.errorMsg =
            err.response?.data?.message || "Errore durante la registrazione";
        })
        .finally(() => {
          this.loadingSalva = false;
        });
    },
    // Genera un piano di N rate pianificate (non ancora pagate) che
    // spacca il residuo attuale — round 54-bis. Componibile con rate già
    // registrate: non azzera né ricalcola da capo, aggiunge solo.
    suddividiInRate() {
      if (!this.numeroRatePianificate || this.numeroRatePianificate < 1) {
        this.errorMsg = "Serve almeno 1 rata";
        return;
      }
      this.loadingSuddividi = true;
      this.errorMsg = null;
      this.warningMsg = null;
      this.$store
        .dispatch("crea_rate_pianificate", {
          id_fattura: this.contabile.id,
          numero_rate: this.numeroRatePianificate,
          // round 56-ter: opzionali, solo se l'utente ha scelto una prima
          // scadenza — altrimenti le rate nascono senza scadenza.
          scadenza_iniziale: this.scadenzaIniziale
            ? moment(this.scadenzaIniziale).unix()
            : null,
          intervallo_giorni: this.intervalloGiorni || 30,
        })
        .then(() => {
          this.caricaDati(true);
          this.$emit("cambiato");
        })
        .catch((err) => {
          this.errorMsg =
            err.response?.data?.message || "Errore durante la suddivisione in rate";
        })
        .finally(() => {
          this.loadingSuddividi = false;
        });
    },
    // Colore del testo della scadenza di una rata non ancora pagata
    // (round 56-ter): rosso se scaduta, verde se oggi/domani, grigio se
    // più lontana — una rata già pagata non ha bisogno di urgenza, resta
    // sempre neutra.
    coloreScadenza(rata) {
      if (!rata.scadenza || rata.saldato) return "";
      const oggi = moment().startOf("day");
      const scadenza = moment.unix(rata.scadenza).startOf("day");
      const giorni = scadenza.diff(oggi, "days");
      if (giorni < 0) return "red--text";
      if (giorni <= 1) return "green--text";
      return "grey--text";
    },
    formatScadenza(ts) {
      return ts ? moment.unix(ts).format("DD/MM/YYYY") : "";
    },
    // Formato "YYYY-MM-DD" richiesto da v-date-picker.
    scadenzaISO(ts) {
      return ts ? moment.unix(ts).format("YYYY-MM-DD") : null;
    },
    // Imposta/rimuove la scadenza di una rata già registrata.
    modificaScadenza(rata, valoreData) {
      this.loadingScadenzaRata = rata.id;
      this.$store
        .dispatch("set_scadenza_allocazione", {
          id_fattura: this.contabile.id,
          id: rata.id,
          scadenza: valoreData ? moment(valoreData).unix() : null,
        })
        .then(() => {
          this.menuScadenzaRataAperto = null;
          this.caricaDati(true);
        })
        .catch((err) => {
          this.errorMsg =
            err.response?.data?.message || "Errore durante l'aggiornamento della scadenza";
        })
        .finally(() => {
          this.loadingScadenzaRata = null;
        });
    },
    // Corregge la data di incasso di una rata già saldata (round
    // 65-undecies) — es. inserita in ritardo con la data sbagliata. Non
    // tocca saldato/metodo: per quello c'è segnaRataSaldata sotto.
    modificaTsSaldo(rata, valoreData) {
      if (!valoreData) return; // la data di incasso non si può cancellare, solo correggere
      this.loadingTsSaldoRata = rata.id;
      this.$store
        .dispatch("set_ts_saldo_allocazione", {
          id_fattura: this.contabile.id,
          id: rata.id,
          ts_saldo: moment(valoreData).unix(),
        })
        .then(() => {
          this.menuTsSaldoRataAperto = null;
          this.caricaDati(true);
          // A differenza della scadenza (puramente informativa, nessun
          // altro punto la mostra), una correzione qui può cambiare il
          // ts_saldo dei debiti finanziati dalla fattura (round
          // 65-quaterdecies) — senza questo evento la loro riga nella
          // lista principale, fuori da questo dialog, restava con il
          // valore vecchio finché non scattava un refresh per un altro
          // motivo (rientro del focus sulla finestra, un'altra azione).
          this.$emit("cambiato");
        })
        .catch((err) => {
          this.errorMsg =
            err.response?.data?.message || "Errore durante l'aggiornamento della data di incasso";
        })
        .finally(() => {
          this.loadingTsSaldoRata = null;
        });
    },
    // Corregge la data di una copertura "normale" (pagamento fresco o
    // erosione di credito) già registrata su un Debito, in "Coperture
    // registrate" — round 65-octovicies, stesso motivo/pattern di
    // modificaTsSaldo sopra ma per un'Allocazione che non è una rata
    // (nessun concetto di saldato/non saldato qui, solo la data).
    modificaTsCopertura(allocazione, valoreData) {
      if (!valoreData) return;
      this.loadingTsCopertura = allocazione.id;
      this.$store
        .dispatch("set_ts_allocazione", {
          id_debito: this.contabile.id,
          id: allocazione.id,
          ts: moment(valoreData).unix(),
        })
        .then(() => {
          this.menuTsCoperturaAperto = null;
          this.caricaDati(true);
          // Il debito può aggiornare il proprio ts_saldo di conseguenza
          // (round 65-quaterdecies) — stesso motivo dell'emit in
          // modificaTsSaldo sopra.
          this.$emit("cambiato");
        })
        .catch((err) => {
          this.errorMsg =
            err.response?.data?.message || "Errore durante l'aggiornamento della data";
        })
        .finally(() => {
          this.loadingTsCopertura = null;
        });
    },
    // Corregge il metodo di pagamento di una copertura "normale" già
    // registrata su un Debito, in "Coperture registrate" — round
    // 65-tricies. Il menu che la richiama non compare per un'erosione di
    // credito (a.pagamento_origine valorizzato), che non ha un metodo
    // proprio — il backend la rifiuterebbe comunque.
    modificaMetodoCopertura(allocazione, metodo) {
      this.loadingMetodoCopertura = allocazione.id;
      this.$store
        .dispatch("set_metodo_allocazione", {
          id_debito: this.contabile.id,
          id: allocazione.id,
          metodo_pagamento: metodo,
        })
        .then(() => {
          this.caricaDati(true);
          // Il "Metodo" della lista principale (fuori da questo dialog)
          // può cambiare — round 65-tricies mostra "Misto" quando le
          // coperture di un debito hanno metodi diversi, "Metodo X"
          // quando ne resta uno solo.
          this.$emit("cambiato");
        })
        .catch((err) => {
          this.errorMsg =
            err.response?.data?.message || "Errore durante l'aggiornamento del metodo";
        })
        .finally(() => {
          this.loadingMetodoCopertura = null;
        });
    },
    // Segna una singola rata pianificata come pagata (con metodo scelto
    // solo ora) o la riporta a non pagata — round 54-bis.
    segnaRataSaldata(rata, stato, metodoPagamento) {
      this.loadingSaldatoRata = rata.id;
      this.errorMsg = null;
      this.$store
        .dispatch("set_saldato_allocazione", {
          id_fattura: this.contabile.id,
          id: rata.id,
          stato,
          metodo_pagamento: metodoPagamento,
        })
        .then(() => {
          this.caricaDati(true);
          this.$emit("cambiato");
        })
        .catch((err) => {
          this.errorMsg =
            err.response?.data?.message || "Errore durante l'aggiornamento della rata";
        })
        .finally(() => {
          this.loadingSaldatoRata = null;
        });
    },
    eliminaAllocazione(id) {
      this.loadingElimina = id;
      this.$store
        .dispatch("elimina_allocazione", id)
        .then(() => {
          this.caricaDati(true);
          this.$emit("cambiato");
        })
        .catch(() => {
          this.errorMsg = "Errore durante l'eliminazione";
        })
        .finally(() => {
          this.loadingElimina = null;
        });
    },
    // Conferma prima di eliminare una rata (round 65-undecies): un click
    // sull'icona non cancella più subito, apre prima il dialog.
    richiediEliminaRata(rata) {
      this.rataDaEliminare = rata;
      this.dialogConfermaEliminaRata = true;
    },
    confermaEliminaRata() {
      if (this.rataDaEliminare) {
        this.eliminaAllocazione(this.rataDaEliminare.id);
      }
      this.dialogConfermaEliminaRata = false;
      this.rataDaEliminare = null;
    },
    estinguiCredito() {
      this.loadingEstingui = true;
      this.errorMsg = null;
      this.$store
        .dispatch("estingui_credito", this.contabile.id)
        .then(() => {
          this.dialogEstingui = false;
          this.caricaDati(true);
          this.$emit("cambiato");
        })
        .catch((err) => {
          this.errorMsg =
            err.response?.data?.message || "Errore durante l'estinzione del credito";
          this.dialogEstingui = false;
        })
        .finally(() => {
          this.loadingEstingui = false;
        });
    },
  },
};
